import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, ArrowUpRight } from "lucide-react";
import { useLang } from "@/i18n";
import { MaskedLines, Reveal, FadeIn, Kicker } from "@/components/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { t } = useLang();
  const c = t.contact;
  const [form, setForm] = useState({ name: "", company: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { ...form, source: "contact-page" });
      toast.success(c.form.success);
      setForm({ name: "", company: "", email: "", message: "" });
    } catch {
      toast.error(c.form.error);
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full border border-navy-900/15 bg-white px-4 py-3.5 text-sm outline-none transition-colors focus:border-gold";

  return (
    <>
      <section className="relative overflow-hidden pt-32 pb-16 sm:pt-40 sm:pb-20 grain" data-testid="contact-hero">
        <div className="pointer-events-none absolute -left-40 top-0 h-[420px] w-[420px] rounded-full bg-sage-100/70 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <FadeIn delay={0.1}>
            <Kicker>{c.kicker}</Kicker>
          </FadeIn>
          <MaskedLines
            className="mt-9 max-w-3xl font-serif text-4xl leading-[1.1] tracking-tight text-navy-900 sm:text-5xl lg:text-6xl"
            delay={0.25}
            lines={[c.title]}
          />
          <Reveal delay={0.6}>
            <p className="mt-7 max-w-2xl text-base leading-relaxed text-navy-900/70 sm:text-lg" data-testid="contact-intro">
              {c.intro}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-20 sm:pb-28" data-testid="contact-main">
        <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <h2 className="text-xs uppercase tracking-[0.28em] text-gold-deep">{c.info.title}</h2>
              <div className="mt-6 space-y-6">
                <div className="flex items-start gap-4 border-b border-navy-900/10 pb-6">
                  <MapPin size={18} className="mt-0.5 shrink-0 text-sage-700" />
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.2em] text-navy-900/45">{c.info.locationLabel}</p>
                    <p className="mt-1.5 text-base font-medium text-navy-900" data-testid="contact-location">{c.info.location}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 border-b border-navy-900/10 pb-6">
                  <Phone size={18} className="mt-0.5 shrink-0 text-sage-700" />
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.2em] text-navy-900/45">{c.info.phoneLabel}</p>
                    <a href="tel:+393409726465" className="mt-1.5 block text-base font-medium text-navy-900 transition-colors hover:text-sage-700" data-testid="contact-phone-link">
                      {c.info.phone}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4 border-b border-navy-900/10 pb-6">
                  <Mail size={18} className="mt-0.5 shrink-0 text-sage-700" />
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.2em] text-navy-900/45">{c.info.emailLabel}</p>
                    <a href="mailto:info@eclenia.com" className="mt-1.5 block break-all text-base font-medium text-navy-900 transition-colors hover:text-sage-700" data-testid="contact-email-link">
                      {c.info.email}
                    </a>
                  </div>
                </div>
              </div>
              <p className="mt-8 max-w-sm border-l-2 border-gold pl-5 text-sm leading-relaxed text-navy-900/60" data-testid="contact-note">
                {c.info.note}
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.15}>
              <form onSubmit={submit} className="border border-navy-900/10 bg-white p-8 sm:p-10" data-testid="contact-form">
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-[11px] uppercase tracking-[0.2em] text-navy-900/50">{c.form.name}</label>
                    <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={c.form.namePh} className={inputCls} data-testid="contact-form-name-input" />
                  </div>
                  <div>
                    <label className="mb-2 block text-[11px] uppercase tracking-[0.2em] text-navy-900/50">{c.form.company}</label>
                    <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder={c.form.companyPh} className={inputCls} data-testid="contact-form-company-input" />
                  </div>
                </div>
                <div className="mt-5">
                  <label className="mb-2 block text-[11px] uppercase tracking-[0.2em] text-navy-900/50">{c.form.email}</label>
                  <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={c.form.emailPh} className={inputCls} data-testid="contact-form-email-input" />
                </div>
                <div className="mt-5">
                  <label className="mb-2 block text-[11px] uppercase tracking-[0.2em] text-navy-900/50">{c.form.message}</label>
                  <textarea required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder={c.form.messagePh} className={`${inputCls} resize-none`} data-testid="contact-form-message-input" />
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-form-submit-btn"
                  className="group mt-7 flex w-full items-center justify-center gap-2.5 bg-navy-900 py-4 text-sm font-medium tracking-wide text-cream transition-colors duration-300 hover:bg-sage-700 disabled:opacity-60"
                >
                  {sending ? c.form.sending : c.form.submit}
                  <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
